/**
 * Sibling swatches on product cards (snippets/sibling-swatches.liquid, context: 'card').
 *
 * Clicking a swatch swaps the whole card for the sibling's card - image, badges, title, price,
 * rating, add to cart / quick view - rendered server-side with the exact params of the current
 * card (templates/search.sibling-card.liquid). Falls back to navigating to the sibling on failure.
 * Delegated from document, so cards added later (filters, infinite scroll, swaps) work as is.
 */
if (!window.siblingSwatchesLoaded) {
  window.siblingSwatchesLoaded = true;

  const fragmentCache = new Map();
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const FADE_MS = 250; // image fade, matches .sibling-image-fade in assets/sibling-swatches.css
  const LOADING_DELAY_MS = 120; // dim the card only if the fetch is slow (not prefetched)
  const IMAGE_TIMEOUT_MS = 700;
  const QUIET_MS = 120;
  const SETTLE_MAX_MS = 1000;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Warm the browser cache with the new card's main image so it doesn't appear empty
  const preloadImage = (fragment) => {
    const source = fragment.querySelector('.card-product img');
    if (!source) return Promise.resolve();
    const image = new Image();
    if (source.sizes) image.sizes = source.sizes;
    if (source.srcset) image.srcset = source.srcset;
    image.src = source.getAttribute('src') || '';
    const loaded = image.decode ? image.decode().catch(() => {}) : Promise.resolve();
    return Promise.race([loaded, wait(IMAGE_TIMEOUT_MS)]);
  };

  // The theme injects parts of a card after it enters the viewport (badges below the image via
  // $store.xBadges, on x-intersect), which resizes the image area. How long that takes varies by
  // device, so a fixed number of frames is not enough: wait until no nodes have been added for
  // QUIET_MS and the layout has held still for a few frames (capped at SETTLE_MAX_MS).
  const settle = (card) =>
    new Promise((resolve) => {
      const start = performance.now();
      let lastMutation = start;
      let lastSize = '';
      let stableFrames = 0;
      const observer = new MutationObserver(() => {
        lastMutation = performance.now();
      });
      observer.observe(card, { childList: true, subtree: true, characterData: true });

      const measure = () => {
        const images = [...card.querySelectorAll('img')].map((img) => Math.round(img.getBoundingClientRect().height));
        return `${Math.round(card.getBoundingClientRect().height)}:${Math.max(0, ...images)}`;
      };

      const tick = () => {
        const now = performance.now();
        const size = measure();
        stableFrames = size === lastSize ? stableFrames + 1 : 0;
        lastSize = size;
        const quiet = now - lastMutation >= QUIET_MS;
        if ((quiet && stableFrames >= 3) || now - start >= SETTLE_MAX_MS) {
          observer.disconnect();
          resolve();
        } else {
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);
    });

  // The frozen card is positioned against the card's parent. Counted, so overlapping swaps on
  // cards sharing a parent don't reset it underneath each other.
  const positionParent = (parent) => {
    const active = +(parent.dataset.siblingSwaps || 0);
    if (active === 0 && getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
      parent.dataset.siblingPositioned = 'true';
    }
    parent.dataset.siblingSwaps = active + 1;
    return () => {
      const remaining = +parent.dataset.siblingSwaps - 1;
      parent.dataset.siblingSwaps = remaining;
      if (remaining === 0 && parent.dataset.siblingPositioned) {
        parent.style.position = '';
        delete parent.dataset.siblingPositioned;
      }
    };
  };

  const offset = (rect, parentRect) => ({
    top: `${rect.top - parentRect.top}px`,
    left: `${rect.left - parentRect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  });

  // Keep the old card on top, in place and unchanged, while the new one is staged underneath.
  // Its ids stay: the theme styles some card parts by id, and the new card is earlier in the DOM,
  // so getElementById already resolves to it.
  const freeze = (card) => {
    Object.assign(card.style, offset(card.getBoundingClientRect(), card.parentElement.getBoundingClientRect()));
    card.classList.add('is-sibling-frozen');
  };

  // The card's main image as currently painted: the largest visible <img>
  const visibleImage = (card) => {
    let best = null;
    let bestArea = 0;
    card.querySelectorAll('img').forEach((img) => {
      const rect = img.getBoundingClientRect();
      const style = getComputedStyle(img);
      const area = rect.width * rect.height;
      if (area > bestArea && style.visibility === 'visible' && +style.opacity > 0) {
        best = img;
        bestArea = area;
      }
    });
    return best;
  };

  // Lay a copy of the old main image over the new main image, inside the new card's own image
  // container - so it inherits the theme's clipping and rounded corners - to fade it out there.
  const overlayOldImage = (oldImage, newCard, startOpacity) => {
    const newImage = visibleImage(newCard);
    const host = newImage && newImage.offsetParent;
    if (!oldImage || !oldImage.complete || !host || !newCard.contains(host)) return null;

    const copy = oldImage.cloneNode();
    copy.removeAttribute('id');
    copy.removeAttribute('loading');
    copy.removeAttribute('onload');
    copy.className = 'sibling-image-fade';
    Object.assign(copy.style, {
      top: `${newImage.offsetTop}px`,
      left: `${newImage.offsetLeft}px`,
      width: `${newImage.offsetWidth}px`,
      height: `${newImage.offsetHeight}px`,
      objectFit: getComputedStyle(newImage).objectFit,
      objectPosition: getComputedStyle(newImage).objectPosition,
      opacity: startOpacity,
    });
    host.appendChild(copy);
    return copy;
  };

  const fragmentUrl = (container, handle) => {
    const terms = `sibcard;handle=${encodeURIComponent(handle)};${container.dataset.siblingParams || ''}`;
    return `${root}search?view=sibling-card&type=product&q=${encodeURIComponent(terms)}`;
  };

  const fetchCard = (url) => {
    if (fragmentCache.has(url)) return fragmentCache.get(url);

    const promise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const card = doc.querySelector('[data-sibling-fragment] .card-product');
        if (!card) throw new Error('Sibling card not found');
        return card.outerHTML;
      })
      .catch((error) => {
        fragmentCache.delete(url);
        throw error;
      });

    fragmentCache.set(url, promise);
    return promise;
  };

  const getContext = (swatch) => {
    const container = swatch.closest('[data-sibling-card]');
    const card = swatch.closest('.card-product');
    if (!container || !card || !swatch.dataset.siblingHandle) return null;
    return { container, card, url: fragmentUrl(container, swatch.dataset.siblingHandle) };
  };

  const prefetch = (event) => {
    const swatch = event.target.closest && event.target.closest('[data-sibling-card] .sibling-swatch');
    if (!swatch || swatch.getAttribute('aria-current') === 'true') return;
    const context = getContext(swatch);
    if (context) fetchCard(context.url).catch(() => {});
  };

  const select = async (event) => {
    const swatch = event.target.closest('[data-sibling-card] .sibling-swatch');
    if (!swatch) return;
    event.preventDefault();
    event.stopPropagation();
    if (swatch.getAttribute('aria-current') === 'true') return;

    const context = getContext(swatch);
    if (!context) {
      window.location.href = swatch.href;
      return;
    }

    const { card, url } = context;
    card.dataset.siblingPending = url;
    const loadingTimer = setTimeout(() => card.classList.add('is-sibling-loading'), LOADING_DELAY_MS);

    const template = document.createElement('template');
    try {
      template.innerHTML = await fetchCard(url);
      await preloadImage(template.content);
    } catch (error) {
      window.location.href = swatch.href;
      return;
    } finally {
      clearTimeout(loadingTimer);
    }

    // Another swatch on the same card was clicked meanwhile - drop this result
    if (card.dataset.siblingPending !== url || !card.isConnected) return;

    const newCard = template.content.firstElementChild;
    const parent = card.parentElement;
    const restoreParent = positionParent(parent);

    // 1. Stage: the new card goes in at opacity 0, in its own stacking context (the theme's inner
    //    z-indexes can't paint over the old card), and the theme initializes it there - badges,
    //    sliders, image height. Not visibility: hidden - theme children with visibility: visible
    //    override it and showed through the old card's transparent areas. Not display: none -
    //    x-intersect must still fire. The old card stays on top, untouched.
    freeze(card);
    newCard.classList.add('is-sibling-staged');
    card.before(newCard);
    await settle(newCard);

    // 2. Swap in one frame: text, price, badges and swatches change instantly (crossfading two
    //    different cards ghosts the text). Only the image transitions: a copy of the old image is
    //    laid over the new one, inside the new card's image container, and fades out.
    const oldImage = reducedMotion.matches ? null : visibleImage(card);
    const startOpacity = getComputedStyle(card).opacity; // the old card may be dimmed by the loading state
    card.remove();
    newCard.classList.remove('is-sibling-staged');
    restoreParent();
    const fade = oldImage ? overlayOldImage(oldImage, newCard, startOpacity) : null;

    if (fade) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fade.style.opacity = '0';
        });
      });
      setTimeout(() => fade.remove(), FADE_MS + 50);
    }

    newCard.dispatchEvent(
      new CustomEvent('sibling:change', {
        bubbles: true,
        detail: { handle: swatch.dataset.siblingHandle, url: swatch.href },
      })
    );
  };

  document.addEventListener('click', select);
  document.addEventListener('mouseover', prefetch);
  document.addEventListener('focusin', prefetch);
  document.addEventListener('touchstart', prefetch, { passive: true });
}
